import { useCallback, useEffect, useState } from 'react';
import { type Room, RoomEvent, type RemoteParticipant } from 'livekit-client';
import type { ChatMessage } from '@aslgo/shared';

/**
 * 1:1 chat over the call room's data channel. Each `ChatMessage` is sent as
 * reliable JSON via `room.localParticipant.publishData`. Messages are kept in
 * arrival order in local state — both peers see the same conversation because
 * each side mirrors locally and broadcasts to the room. No backend storage;
 * conversation is ephemeral and ends with the call.
 *
 * `userId` is the local user's auth uuid, and every message's `from` is a uuid
 * too — a key for "is this mine?", never something to render. ChatPanel resolves
 * the sender's name.
 */
export function useCallChat(room: Room | null, userId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  useEffect(() => {
    if (!room) {
      setMessages([]);
      return;
    }
    const handler = (payload: Uint8Array, participant?: RemoteParticipant) => {
      let msg: unknown;
      try { msg = JSON.parse(new TextDecoder().decode(payload)); } catch { return; }
      const m = msg as Partial<ChatMessage>;
      if (m?.type !== 'chat' || typeof m.text !== 'string') return;
      const incoming: ChatMessage = {
        type: 'chat',
        from: participant?.identity ?? m.from ?? '?',
        text: m.text,
        ts: typeof m.ts === 'number' ? m.ts : Date.now(),
      };
      setMessages((prev) => [...prev, incoming]);
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => { room.off(RoomEvent.DataReceived, handler); };
  }, [room]);

  const send = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!room || !userId || !trimmed) return;
    const msg: ChatMessage = { type: 'chat', from: userId, text: trimmed, ts: Date.now() };
    const data = new TextEncoder().encode(JSON.stringify(msg));
    void room.localParticipant.publishData(data, { reliable: true });
    setMessages((prev) => [...prev, msg]);
  }, [room, userId]);

  return { messages, send };
}
